/**
 * JWT auth client — matches the Gontec_Mobile_New auth model 1:1.
 *
 * Endpoint contract (Django simplejwt-style):
 *   POST <base>/api/token/         body { email, password } → { access, refresh }
 *   POST <base>/api/token/refresh/ body { refresh }          → { access }
 *   GET  <base>/users/me/          → user record
 *
 * Auth header is `Authorization: JWT <access>` (NOT Bearer — that's what
 * the Django simplejwt configuration on this backend expects).
 *
 * Base URL must come from `VITE_WEB_AUTH_API_BASE` (set in `.env` — see
 * `.env.example`).  We deliberately do not bake a default URL into the
 * source to keep credentials from being sent to a stale endpoint when
 * the env var is missing.
 */

const STORAGE_KEY = 'rl_auth_tokens_v1'
const USER_STORAGE_KEY = 'rl_auth_user_v1'
const LOGIN_PATH = '/api/token/'
const REFRESH_PATH = '/api/token/refresh/'
const ME_PATH = '/users/me/'

export interface AuthTokens {
  access: string
  refresh: string
}

interface StoredTokens extends AuthTokens {
  savedAt: number
}

export interface AuthUser {
  id?: number | string
  email?: string
  first_name?: string
  last_name?: string
  [key: string]: unknown
}

function getBaseUrl(): string {
  // Empty / unset → same-origin (Vite dev-proxy or production reverse proxy
  // handles the routing).  Strip any trailing slash *and* a trailing /api so
  // callers can safely paste either "https://host" or "https://host/api/" —
  // we own the /api/ prefix on the request paths below.
  const raw = (import.meta.env.VITE_WEB_AUTH_API_BASE ?? '') as string
  return raw.replace(/\/+$/, '').replace(/\/api$/, '')
}

// ─── token + user persistence ───────────────────────────────────────────────

let cached: AuthTokens | null = readFromStorage()
let cachedUser: AuthUser | null = readUserFromStorage()

function readFromStorage(): AuthTokens | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredTokens
    if (typeof parsed.access !== 'string' || typeof parsed.refresh !== 'string') {
      return null
    }
    return { access: parsed.access, refresh: parsed.refresh }
  } catch {
    return null
  }
}

function readUserFromStorage(): AuthUser | null {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY)
    return raw ? (JSON.parse(raw) as AuthUser) : null
  } catch {
    return null
  }
}

function writeToStorage(t: AuthTokens | null): void {
  cached = t
  try {
    if (t) {
      const stored: StoredTokens = { ...t, savedAt: Date.now() }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stored))
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  } catch {
    /* private browsing or quota — fall back to memory only */
  }
}

function writeUserToStorage(u: AuthUser | null): void {
  cachedUser = u
  try {
    if (u) localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(u))
    else localStorage.removeItem(USER_STORAGE_KEY)
  } catch {
    /* memory-only fallback */
  }
}

// ─── public API ─────────────────────────────────────────────────────────────

export function isAuthenticated(): boolean {
  return cached != null && cached.access.length > 0
}

export function getAccessToken(): string | null {
  return cached?.access ?? null
}

export function getCurrentUser(): AuthUser | null {
  return cachedUser
}

export async function login(email: string, password: string): Promise<void> {
  const resp = await fetch(getBaseUrl() + LOGIN_PATH, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })
  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(`Login failed (HTTP ${resp.status}): ${text || resp.statusText}`)
  }
  const data = (await resp.json()) as { access?: string; refresh?: string }
  if (!data.access || !data.refresh) {
    throw new Error('Login response missing access/refresh tokens')
  }
  writeToStorage({ access: data.access, refresh: data.refresh })

  // Fetch user info — non-blocking; if it fails we're still logged in.
  try {
    const me = await authenticatedFetch(ME_PATH)
    if (me.ok) {
      writeUserToStorage((await me.json()) as AuthUser)
    }
  } catch {
    /* user metadata is optional */
  }
}

export function logout(): void {
  writeToStorage(null)
  writeUserToStorage(null)
}

export async function refreshAccessToken(): Promise<boolean> {
  if (!cached?.refresh) return false
  try {
    const resp = await fetch(getBaseUrl() + REFRESH_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: cached.refresh }),
    })
    if (!resp.ok) return false
    const data = (await resp.json()) as { access?: string; refresh?: string }
    if (!data.access) return false
    writeToStorage({
      access: data.access,
      refresh: data.refresh ?? cached.refresh,
    })
    return true
  } catch {
    return false
  }
}

/**
 * Fetch with Bearer header + auto-refresh.  On any 401 we try refresh
 * once and replay; if the second attempt also returns 401 we clear
 * tokens (forcing a re-login) and throw.
 */
export async function authenticatedFetch(
  pathOrUrl: string,
  init: RequestInit = {},
): Promise<Response> {
  if (!cached) {
    throw new Error('Not authenticated')
  }
  const url = pathOrUrl.startsWith('http')
    ? pathOrUrl
    : getBaseUrl() + (pathOrUrl.startsWith('/') ? '' : '/') + pathOrUrl

  const withAuth = (): RequestInit => ({
    ...init,
    headers: {
      ...(init.headers as Record<string, string> | undefined),
      Authorization: `JWT ${cached!.access}`,
    },
  })

  let resp = await fetch(url, withAuth())
  if (resp.status === 401) {
    const refreshed = await refreshAccessToken()
    if (!refreshed) {
      writeToStorage(null)
      writeUserToStorage(null)
      throw new Error('Session expired')
    }
    resp = await fetch(url, withAuth())
    if (resp.status === 401) {
      writeToStorage(null)
      writeUserToStorage(null)
      throw new Error('Session expired')
    }
  }
  return resp
}
